import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl-next';
import ButtonGroup from '@atlaskit/button/button-group';
import Button, { LoadingButton } from '@atlaskit/button';
import { R400 } from '@atlaskit/theme/colors';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import ErrorIcon from '@atlaskit/icon/core/error';
import { token } from '@atlaskit/tokens';

import messages from './messages';
import { CONNECT_ERROR, FETCH_ERROR } from '../../../state/ui/connect-space/types';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

import { Container, WrappedError } from './styled';
import { type Props } from './types';

const Footer = ({
	isSubmitAllowed,
	isSubmitting,
	errorState,
	selectedSpace,
	onFetch,
	onConnect,
	onCancel,
	intl: { formatMessage },
}: Props) => {
	const [hasError, setHasError] = useState<boolean>(false);

	useEffect(() => {
		const test = errorState === CONNECT_ERROR || errorState === FETCH_ERROR || false;
		setHasError(test);
	}, [errorState]);

	const handleSubmit = (analyticsEvent: UIAnalyticsEvent) => {
		const isPageSelected = selectedSpace?.isPage;
		if (errorState === CONNECT_ERROR) {
			onConnect();
			fireUIAnalytics(analyticsEvent, 'retryConnectButton', { isPageSelected });
		} else if (errorState === FETCH_ERROR) {
			onFetch();
			fireUIAnalytics(analyticsEvent, 'retryFetchButton', { isPageSelected });
		} else {
			onConnect();
			fireUIAnalytics(analyticsEvent, 'connectButton', { isPageSelected });
		}
	};

	return (
		<Container>
			<WrappedError>
				{hasError && (
					<ErrorIcon
						spacing="spacious"
						label={formatMessage(messages.error)}
						color={token('color.icon.danger', R400)}
					/>
				)}
				{hasError && formatMessage(messages.connectionError)}
			</WrappedError>
			<ButtonGroup>
				<Button
					appearance="link"
					isDisabled={isSubmitting}
					onClick={(_, analyticsEvent) => {
						onCancel();
						fireUIAnalytics(analyticsEvent, 'cancelButton');
					}}
				>
					{formatMessage(messages.cancel)}
				</Button>
				<LoadingButton
					data-test-id="confluence-project-pages.post-expand.connect-or-update-space-modal.connect-button"
					appearance="primary"
					isLoading={isSubmitting}
					isDisabled={!isSubmitAllowed}
					onClick={(_, analyticsEvent) => handleSubmit(analyticsEvent)}
				>
					{hasError ? formatMessage(messages.retry) : formatMessage(messages.connectSpace)}
				</LoadingButton>
			</ButtonGroup>
		</Container>
	);
};

export default injectIntl(Footer);
